import { useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, KeyRound, Loader2, PlugZap, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { AiModelDraft, AiProviderId, AiProviderInfo, AiTestResult } from "@/lib/callables";
import {
  credentialTag,
  hasSavedKey,
  healthUntilLabel,
  inheritSourceIndex,
  inheritsKey,
  savedKeyEnd,
  savedKeyMask,
  type AiEntryDraft,
} from "@/lib/aiSettings";
import { AiModelSelect } from "./AiModelSelect";

interface AiModelEntryCardProps {
  entries: AiEntryDraft[];
  index: number;
  providers: AiProviderInfo[];
  models: AiModelDraft[];
  /** O momento da lista, para a pausa do modelo não depender do render. */
  now: number;
  onChange: (patch: Partial<AiEntryDraft>) => void;
  onRemove?: () => void;
  onTest: () => void;
  isTesting: boolean;
  testResult: AiTestResult | null;
}

/** Um item da fila de modelos: provedor, modelo, chave, a saúde e o teste. */
export function AiModelEntryCard({
  entries,
  index,
  providers,
  models,
  now,
  onChange,
  onRemove,
  onTest,
  isTesting,
  testResult,
}: AiModelEntryCardProps) {
  const entry = entries[index];
  const provider = providers.find((item) => item.id === entry.provider) ?? providers[0];
  const source = inheritSourceIndex(entries, index);
  const inherit = inheritsKey(entries, index);
  const savedKey = hasSavedKey(entry);
  const pause = entry.health ? healthUntilLabel(entry.health, now) : null;
  const idPrefix = `ia-${entry.key}`;
  // Com chave salva, o campo aparece cheio (a máscara, não a chave). Ao
  // entrar nele, fica vazio para colar outra; ao sair sem colar nada, a
  // máscara volta e a chave salva continua.
  const [isEditingKey, setIsEditingKey] = useState(false);
  const showSavedMask = savedKey && !entry.apiKey && !isEditingKey;
  const sourceEntry = source !== null ? entries[source] : null;
  const sourceEnd =
    sourceEntry && !sourceEntry.apiKey.trim() && hasSavedKey(sourceEntry) ? savedKeyEnd(sourceEntry) : "";

  function changeProvider(next: AiProviderId) {
    const info = providers.find((item) => item.id === next);
    if (!info || next === entry.provider) return;
    // Trocar o provedor zera o modelo e a chave: a chave de um não serve no outro.
    const draft = entries.map((item, i) => (i === index ? { ...item, provider: next } : item));
    onChange({
      provider: next,
      model: info.defaultModel,
      customModel: false,
      apiKey: "",
      useOwnKey: inheritSourceIndex(draft, index) === null,
    });
  }

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-caption font-bold">
          {index + 1}
        </span>
        <p className="min-w-0 flex-1 truncate text-small font-bold">
          {index === 0 ? "Modelo principal" : `Modelo ${index + 1} (reserva)`}
        </p>
        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label={`Remover o modelo ${index + 1}`}
          >
            <Trash2 className="size-4" />
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-provedor`}>Provedor</Label>
          <Select value={entry.provider} onValueChange={(value) => changeProvider(value as AiProviderId)}>
            <SelectTrigger id={`${idPrefix}-provedor`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {providers.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <AiModelSelect
          inputId={`${idPrefix}-modelo`}
          provider={provider}
          model={entry.model}
          customModel={entry.customModel}
          credential={credentialTag(entries, index)}
          models={models}
          index={index}
          onChange={onChange}
        />
      </div>

      {inherit && source !== null ? (
        <div className="flex flex-col gap-2 rounded-md bg-muted px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-small text-muted-foreground">
            <KeyRound className="size-4 shrink-0" />
            {sourceEnd
              ? `Usa a mesma chave do modelo ${source + 1}, terminando em ${sourceEnd}`
              : `Usa a mesma chave do modelo ${source + 1}`}
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ useOwnKey: true })}>
            Usar outra chave
          </Button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-chave`}>Chave de API</Label>
          <Input
            id={`${idPrefix}-chave`}
            // A máscara vai como texto (o fim da chave aparece); a chave
            // digitada, como senha.
            type={showSavedMask ? "text" : "password"}
            value={showSavedMask ? savedKeyMask(entry) : entry.apiKey}
            onFocus={() => setIsEditingKey(true)}
            onBlur={() => setIsEditingKey(false)}
            onChange={(event) => onChange({ apiKey: event.target.value })}
            // Curto para caber em 360 px.
            placeholder={savedKey ? "Cole a nova chave" : "Cole a chave de API"}
            aria-label={
              showSavedMask
                ? `Chave de API salva, terminando em ${savedKeyEnd(entry)}. Clique para trocar`
                : undefined
            }
            autoComplete="off"
            spellCheck={false}
          />
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <p className="text-caption text-muted-foreground">
              {showSavedMask
                ? "Chave salva: ela continua ao trocar o modelo, sem digitar de novo. Para usar outra, clique no campo e cole a nova. "
                : savedKey && !entry.apiKey
                  ? "Sem colar nada, a chave salva continua. "
                  : savedKey
                    ? "A chave digitada substitui a salva ao salvar. "
                    : ""}
              Guardada no servidor; usada apenas para chamar a IA deste provedor.
            </p>
            <div className="flex flex-wrap gap-x-3">
              {source !== null ? (
                <button
                  type="button"
                  className="text-caption font-semibold text-primary underline-offset-4 hover:underline"
                  onClick={() => onChange({ useOwnKey: false, apiKey: "" })}
                >
                  Usar a chave do modelo {source + 1}
                </button>
              ) : null}
              <a
                href={provider.keyUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-caption font-semibold text-primary underline-offset-4 hover:underline"
              >
                Onde criar a chave
                <ExternalLink className="size-3" />
              </a>
            </div>
          </div>
        </div>
      )}

      {pause && entry.health ? (
        <div className="rounded-md border border-state-warning/40 bg-state-warning/10 p-3">
          <p className="flex items-start gap-2 text-caption">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-state-warning" />
            <span>
              <strong>
                {pause}: {entry.health.label}.
              </strong>{" "}
              Enquanto isso, as chamadas começam pelo próximo modelo da fila. {entry.health.message}
            </span>
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button type="button" variant="outline" size="sm" onClick={onTest} disabled={isTesting}>
          {isTesting ? <Loader2 className="size-4 animate-spin" /> : <PlugZap className="size-4" />}
          Testar este modelo
        </Button>
        {testResult ? (
          testResult.ok ? (
            <p className="flex items-center gap-1.5 text-caption text-state-ok">
              <CheckCircle2 className="size-4 shrink-0" />
              Respondeu em {(testResult.latencyMs / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} s.
            </p>
          ) : (
            <p className="text-caption text-state-error">
              {testResult.label}: {testResult.message}
            </p>
          )
        ) : null}
      </div>
    </div>
  );
}
